import React, { useState } from 'react';
import { useLocale } from '../i18n/LocaleContext';
import { getEventImageUrl, getFallbackImageUrl } from '../utils/eventImage';
import { getCategoryForType } from '../utils/eventCategories';
import {
  formatCardDate,
  formatDistance,
  formatPrice,
  formatTime,
  getTicketStatus
} from '../utils/format';
import FavouriteButton from './FavouriteButton';

export default function EventCard({ event, onClick, muted = false, note = null, featured = false }) {
  const { locale, t } = useLocale();
  const [imageSrc, setImageSrc] = useState(() => getEventImageUrl(event));

  const category = getCategoryForType(event.event_type);
  const ticketStatus = getTicketStatus(event.tickets_available);
  const isFree = event.lowest_price === 0;
  const isSoldOut = ticketStatus === 'soldout';
  const distance = formatDistance(event.distanceKm, t);

  const priceClass = isFree
    ? 'card__price card__price--free'
    : event.lowest_price == null
      ? 'card__price card__price--unknown'
      : 'card__price';

  return (
    <article className={`card-wrap${featured ? ' card-wrap--featured' : ''}`}>
      <button
        type="button"
        className={`card${isSoldOut ? ' card--soldout' : ''}${muted ? ' card--muted' : ''}${featured ? ' card--featured' : ''}`}
        onClick={onClick}
      >
        <div className="card__media">
          <div className="card__badges">
            <span>
              {featured && <span className="badge badge--featured">★ {t('featured.badge')}</span>}
              {isFree && <span className="badge badge--free">{t('price.free')}</span>}
              {note && <span className="badge badge--neutral">{note}</span>}
            </span>
            <span>
              {isSoldOut && (
                <span className="badge badge--soldout">{t('tickets.soldout')}</span>
              )}
              {ticketStatus === 'limited' && (
                <span className="badge badge--limited">{t('tickets.limited')}</span>
              )}
            </span>
          </div>
          <img
            className="card__image"
            src={imageSrc}
            alt=""
            loading="lazy"
            // Scraped image hosts go down; fall back to the category photo.
            onError={() => setImageSrc(getFallbackImageUrl(event.event_type))}
          />
        </div>

        <div className="card__body">
          {/* The full date stays on every card, including inside a day group -
              cards get shared, scrolled past and skimmed out of context. */}
          <div className="card__when">
            <span>{formatCardDate(event.date, locale)}</span>
            <span className="card__when-time">{formatTime(event.date, locale)}</span>
          </div>

          <h3 className="card__title">{event.title}</h3>

          <div className="card__where">
            <span className="card__venue">{event.venue}</span>
            {distance && (
              <>
                <span className="card__dot">·</span>
                <span className="card__distance">{distance}</span>
              </>
            )}
          </div>

          <div className="card__footer">
            <span className={priceClass}>
              {formatPrice(event.lowest_price, locale, t)}
            </span>
            <span className="card__tag">
              {category.icon} {t(`cat.${category.id}`)}
            </span>
          </div>
        </div>
      </button>

      <FavouriteButton event={event} />
    </article>
  );
}
